import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ExtractionHistoryItem } from '../types/wiseboard';
import ExtractionHistoryRow from './ExtractionHistoryRow';

interface Props {
  items: ExtractionHistoryItem[];
  isLoading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRetry?: (id: string) => void;
  onViewFile?: (storagePath: string) => void;
}

export function ExtractionHistoryTable({
  items,
  isLoading,
  page,
  totalPages,
  onPageChange,
  onRetry,
  onViewFile
}: Props) {
  if (isLoading && items.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-lg font-medium">No extraction history found</p>
        <p className="text-sm mt-1">Try adjusting your filters or date range</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Document</TableHead>
              <TableHead>Loan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">GPU</TableHead>
              <TableHead className="text-center">Pages</TableHead>
              <TableHead className="text-center">Time</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <ExtractionHistoryRow
                key={item.id}
                item={item}
                onRetry={onRetry}
                onViewFile={onViewFile}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-1">
            {/* First page */}
            {page > 3 && (
              <>
                <Button
                  variant={page === 1 ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onPageChange(1)}
                  className="w-8 h-8 p-0"
                >
                  1
                </Button>
                {page > 4 && <span className="px-2 text-muted-foreground">...</span>}
              </>
            )}

            {/* Page numbers around current */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              if (pageNum < 1 || pageNum > totalPages) {
return null;
}
              if (pageNum === 1 && page > 3) {
return null;
}
              if (pageNum === totalPages && page < totalPages - 2) {
return null;
}

              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className="w-8 h-8 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}

            {/* Last page */}
            {page < totalPages - 2 && totalPages > 5 && (
              <>
                {page < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                <Button
                  variant={page === totalPages ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onPageChange(totalPages)}
                  className="w-8 h-8 p-0"
                >
                  {totalPages}
                </Button>
              </>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages || isLoading}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default ExtractionHistoryTable;
